import { CommandExecutionContext, SelfContainedCommand } from 'types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { entityService } from '../../services/entityService';

export const characterUpdateCommand: SelfContainedCommand = {
  id: 'character_update',
  description: '👤 UPDATE CHARACTER: Modify story character details based on natural language requests.',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  version: 'v1',

  schema: {
    parameters: [
      { 
        name: 'id', 
        type: 'string', 
        description: 'Character entity ID (llmId)', 
        required: true,
        examples: ['char_gray_goat_melon', 'char_wise_tiger_mountain']
      },
      { 
        name: 'updates', 
        type: 'object', 
        description: 'Character skeleton fields to update as a JSON object. Can include: description, appearance{height,eyes,hair,build}, identity{age,species,gender,pronouns}, personality{coreTraits}, arc{motivation,conflict,backstory}, voice{tone,dialogueCues,sampleLine}, role, oneLine', 
        required: true,
        examples: [
          { description: "Standing at 6'2\" with brown eyes", appearance: { height: "6'2\"", eyes: "brown" } },
          { personality: { coreTraits: "Dominant, protective, secretive" }, arc: { motivation: "Unlock the tomb's mysteries" } }
        ]
      },
      { 
        name: 'reasoning', 
        type: 'string', 
        description: 'Brief explanation of the update', 
        required: false,
        examples: ['Updated physical appearance', 'Developed personality traits']
      },
    ]
  },

  examples: {
    good: [
      {
        input: {
          id: 'char_gray_goat_melon',
          updates: {
            description: 'Standing at 6 foot 2 with brown eyes, Dr. Alexander commands attention.',
            appearance: {
              height: "6'2\"",
              eyes: 'brown',
              build: 'athletic'
            },
            identity: {
              age: 40
            }
          },
          reasoning: 'Updated physical appearance based on user request'
        },
        expectedOutput: {
          status: 'success',
          message: 'Updated 3 fields: description, appearance, identity',
          format: 'json'
        },
        description: 'Update multiple character fields at once'
      },
      {
        input: {
          id: 'char_wise_tiger_mountain',
          updates: {
            personality: {
              coreTraits: 'Curious, brave, empathetic with a sharp wit'
            },
            arc: {
              motivation: 'Uncover the truth about her family history',
              conflict: 'Fear of losing her sense of identity'
            }
          },
          reasoning: 'Developed character personality and arc'
        },
        expectedOutput: {
          status: 'success',
          message: 'Updated 2 fields: personality, arc',
          format: 'json'
        },
        description: 'Update personality and character arc'
      }
    ],
    bad: [
      {
        input: {
          id: 'char-john',
          updates: 'make him tall'
        },
        error: 'Invalid updates',
        why: 'updates must be an object, not a string',
        fix: 'Use updates: { appearance: { height: "6ft" } }'
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, updates, reasoning } = params || {};

    try {
      if (!id) {
        return createErrorResult('Missing character ID', 'id parameter is required', 'validation');
      }

      if (!updates || typeof updates !== 'object') {
        return createErrorResult('Invalid updates', 'updates must be an object with character skeleton fields', 'validation');
      }

      console.log('[character_update] Updating character:', {
        id,
        updateFields: Object.keys(updates),
        reasoning
      });

      // Find the character entity
      let characterEntity;
      try {
        characterEntity = await entityService.get(id);
      } catch (error) {
        return createErrorResult(
          'Character not found',
          `Could not find character entity with ID: ${id}`,
          'validation'
        );
      }

      if (characterEntity.entityType !== 'StoryCharacter') {
        return createErrorResult(
          'Invalid entity type',
          `Entity ${id} is type "${characterEntity.entityType}", not "StoryCharacter"`,
          'validation'
        );
      }

      // Deep merge the updates into existing skeleton
      const currentSkeleton = characterEntity.skeleton || {};
      const mergedSkeleton: any = { ...currentSkeleton };

      for (const [key, value] of Object.entries(updates)) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Merge nested objects
          mergedSkeleton[key] = {
            ...(mergedSkeleton[key] || {}),
            ...value
          };
        } else {
          // Direct assignment for primitives and arrays
          mergedSkeleton[key] = value;
        }
      }

      // Update the entity
      await entityService.mergeUpdate(id, {
        skeleton: updates // Use updates directly - backend mergeUpdate handles deep merging
      });

      console.log('[character_update] ✅ Character updated successfully');

      // Build a human-readable summary of changes
      const changedFields = Object.keys(updates);
      const summary = changedFields.length === 1
        ? `Updated ${changedFields[0]}`
        : `Updated ${changedFields.length} fields: ${changedFields.slice(0, 3).join(', ')}${changedFields.length > 3 ? '...' : ''}`;

      return createSuccessResult(
        reasoning || summary,
        {
          type: 'character_update',
          characterId: id,
          characterName: characterEntity.name,
          updatedFields: changedFields,
          updates: updates
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            affectedEntities: [id],
            method: 'character_update'
          }
        }
      );
    } catch (error) {
      console.error('[character_update] Update failed:', error);
      return createErrorResult(
        'Update failed',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

